import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Logo } from "@/components/logo";
import logoLight from "@/assets/capitaldart-logo-light.svg.asset.json";
import { MobileNav } from "@/components/marketing/mobile-nav";
import { useAuth } from "@/hooks/use-auth";

const LOGO_LIGHT = logoLight.url;

const NAVY = "#0B2545";
const NAVY_DEEP = "#07182E";
const PAPER = "#F4F1E8";
const PAPER_2 = "#FBFAF5";
const GOLD = "#C9A24B";
const GOLD_2 = "#D8B260";
const INK = "#14213A";
const INK_2 = "#545C6B";
const INK_MUTED = "#6B7384";
const LINE = "#E4DECF";
const NAVY_TEXT = "#EDEAE0";
const NAVY_TEXT_DIM = "#B6BFCD";
const NAVY_TEXT_DIM_2 = "#9AA5B4";

const FONT_SERIF = "'Spectral', Georgia, serif";
const FONT_SANS = "'IBM Plex Sans', system-ui, sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, monospace";

const MCP_URL = "https://capitaldart.com/mcp";

const TITLE = "Use CapitalDart in ChatGPT | Connector setup";
const DESCRIPTION =
  "Add CapitalDart to ChatGPT as a custom connector. Describe your raise, get ranked investor firms and verified contacts from CapitalDart's directory, and draft outreach you send from your own accounts.";

export const Route = createFileRoute("/connect_/chatgpt")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://capitaldart.com/connect/chatgpt" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://capitaldart.com/connect/chatgpt" }],
  }),
  component: ConnectChatGptPage,
});

function LandingLogo({ height = 32 }: { height?: number }) {
  return (
    <Logo
      src={LOGO_LIGHT}
      alt="CapitalDart"
      height={height}
      style={{ display: "block" }}
      trademarkClassName="text-[#F4F1E8]"
    />
  );
}

function GoldButton({
  children,
  to,
  search,
  style,
}: {
  children: React.ReactNode;
  to?: "/auth" | "/dashboard";
  search?: { mode: "signin" | "signup" };
  style?: React.CSSProperties;
}) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_SANS,
    fontWeight: 600,
    color: NAVY,
    background: GOLD,
    borderRadius: 2,
    letterSpacing: ".01em",
    textDecoration: "none",
    transition: "background .15s",
    ...style,
  };
  const onEnter = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD_2;
  };
  const onLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.background = GOLD;
  };
  if (to && search) {
    return (
      <Link to={to} search={search} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        {children}
      </Link>
    );
  }
  return (
    <Link to={to ?? "/dashboard"} style={base} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
    </Link>
  );
}

function Kicker({
  children,
  color = INK_MUTED,
  style,
}: {
  children: React.ReactNode;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        letterSpacing: ".2em",
        textTransform: "uppercase",
        color,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = React.useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  }
  return (
    <div
      style={{
        marginTop: 16,
        display: "flex",
        alignItems: "stretch",
        border: `1px solid ${LINE}`,
        borderRadius: 4,
        background: PAPER_2,
        maxWidth: 560,
      }}
    >
      <input
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="cd-copy-input"
        style={{
          flex: 1,
          padding: "12px 14px",
          fontFamily: FONT_MONO,
          fontSize: 14,
          background: "transparent",
          border: "none",
          color: INK,
        }}
      />
      <button
        type="button"
        onClick={copy}
        style={{
          padding: "0 18px",
          fontFamily: FONT_SANS,
          fontSize: 13,
          fontWeight: 600,
          color: NAVY,
          background: GOLD,
          border: "none",
          cursor: "pointer",
          letterSpacing: ".02em",
        }}
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Hero() {
  const { session, loading } = useAuth();
  const isAuthed = Boolean(session);
  return (
    <section
      style={{
        background: NAVY,
        backgroundImage: `radial-gradient(120% 80% at 85% -10%, rgba(201,162,75,.12), rgba(11,37,69,0) 60%)`,
        color: NAVY_TEXT,
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px)",
          backgroundSize: "64px 64px",
          pointerEvents: "none",
        }}
      />
      <nav
        className="mx-auto"
        style={{
          maxWidth: 1200,
          position: "relative",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <Link to="/" hash="top" style={{ textDecoration: "none" }}>
          <LandingLogo height={34} />
        </Link>
        <div className="hidden lg:flex" style={{ alignItems: "center", gap: 18 }}>
          {loading ? null : isAuthed ? (
            <GoldButton to="/dashboard" style={{ fontSize: 14.5, padding: "11px 20px" }}>
              Dashboard
            </GoldButton>
          ) : (
            <>
              <Link
                to="/auth"
                search={{ mode: "signin" }}
                style={{
                  color: "#AEB8C7",
                  fontSize: 14.5,
                  fontFamily: FONT_SANS,
                  textDecoration: "none",
                }}
              >
                Sign in
              </Link>
              <GoldButton
                to="/auth"
                search={{ mode: "signup" }}
                style={{ fontSize: 14.5, padding: "11px 20px" }}
              >
                Get started
              </GoldButton>
            </>
          )}
        </div>
        <div className="lg:hidden">
          <MobileNav className="cd-connect-nav-mobile" />
        </div>
      </nav>

      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ maxWidth: 1200, position: "relative", padding: "80px 0 96px" }}
      >
        <div className="max-w-3xl">
          <Kicker color={GOLD} style={{ marginBottom: 22 }}>
            CapitalDart for ChatGPT
          </Kicker>
          <h1
            style={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: "clamp(40px,5vw,62px)",
              lineHeight: 1.04,
              letterSpacing: "-.015em",
              margin: "0 0 20px",
              color: "#F7F5EE",
            }}
          >
            Use CapitalDart in ChatGPT.
          </h1>
          <p
            style={{
              fontSize: 18,
              lineHeight: 1.6,
              color: NAVY_TEXT_DIM,
              maxWidth: 700,
              margin: "0 0 26px",
            }}
          >
            Add CapitalDart to ChatGPT as a custom connector, on any plan that supports custom
            connectors, and ask it to find investors, get verified contacts, and draft outreach
            from CapitalDart's directory. Your deals, delivery tracking, and Investor Readiness
            Reports stay at capitaldart.com.
          </p>
          <p
            style={{
              fontSize: 15.5,
              lineHeight: 1.6,
              color: NAVY_TEXT_DIM,
              maxWidth: 700,
              margin: 0,
            }}
          >
            CapitalDart has been submitted to the ChatGPT connector directory. Until it is
            listed, the steps below are the way to connect.
          </p>
        </div>
        <div style={{ marginTop: 40, textAlign: "center" }}>
          <Link
            to="/connect/capabilities"
            style={{
              fontFamily: FONT_SANS,
              fontSize: 15,
              color: "#F7F5EE",
              textDecoration: `underline ${GOLD}`,
              textUnderlineOffset: 4,
            }}
          >
            Read the full capabilities reference
          </Link>
          <div style={{ marginTop: 14 }}>
            <Link
              to="/connect/claude"
              style={{
                fontFamily: FONT_SANS,
                fontSize: 14.5,
                color: NAVY_TEXT_DIM,
                textDecoration: `underline ${GOLD}`,
                textUnderlineOffset: 4,
              }}
            >
              Setting up Claude instead
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS: Array<{ n: string; title: string; body: React.ReactNode }> = [
  {
    n: "01",
    title: "Open connector settings in ChatGPT",
    body: (
      <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
        In ChatGPT on the web, open Settings, then Apps and connectors. Open Advanced settings
        and turn on developer mode, which is what exposes custom connectors. On a business or
        enterprise workspace, an administrator may need to allow custom connectors first.
      </p>
    ),
  },
  {
    n: "02",
    title: "Create the CapitalDart connector",
    body: (
      <>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
          Choose Create on the Apps and connectors screen. Name it CapitalDart and paste this
          link into the connector URL field. Leave the authentication setting as OAuth.
        </p>
        <CopyField value={MCP_URL} />
      </>
    ),
  },
  {
    n: "03",
    title: "Confirm and create",
    body: (
      <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
        ChatGPT shows a notice about trusting third party connectors, which it shows for every
        custom connector. Review the details, tick the confirmation box, and choose Create.
      </p>
    ),
  },
  {
    n: "04",
    title: "Sign in and approve",
    body: (
      <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
        Choose Connect, sign in with your CapitalDart account or create one, and choose Approve
        on the CapitalDart consent screen. The connection is tied to your CapitalDart account
        and team.
      </p>
    ),
  },
  {
    n: "05",
    title: "Enable it in a conversation and ask",
    body: (
      <>
        <p style={{ margin: 0, fontSize: 15.5, lineHeight: 1.65, color: INK_2 }}>
          Start a new conversation, enable CapitalDart from the plus menu in the message
          composer, then try:
        </p>
        <blockquote
          style={{
            margin: "18px 0 0",
            padding: "18px 22px",
            borderLeft: `3px solid ${GOLD}`,
            background: PAPER_2,
            fontFamily: FONT_SERIF,
            fontSize: 18,
            lineHeight: 1.5,
            color: INK,
            fontStyle: "italic",
          }}
        >
          "I'm raising $10M of equity for a fintech company in New York, targeting family
          offices. Find investors for me."
        </blockquote>
      </>
    ),
  },
];

function Steps() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 18 }}>Setup</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(30px,3.4vw,42px)",
            lineHeight: 1.08,
            letterSpacing: "-.01em",
            margin: "0 0 48px",
          }}
        >
          Five steps, about three minutes.
        </h2>
        <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
          {STEPS.map((s, i) => (
            <li
              key={s.n}
              style={{ padding: "36px 0", borderTop: i === 0 ? "none" : `1px solid ${LINE}` }}
            >
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: 12,
                  letterSpacing: ".2em",
                  color: GOLD,
                  marginBottom: 10,
                }}
              >
                STEP {s.n}
              </div>
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 24,
                  lineHeight: 1.25,
                  margin: "0 0 14px",
                }}
              >
                {s.title}
              </h3>
              {s.body}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const CAPABILITIES: Array<{ title: string; body: string }> = [
  {
    title: "Describe a raise",
    body: "Give ChatGPT a plain-language description or upload your deck and get back a structured targeting plan covering sector, check size, structure, and geography.",
  },
  {
    title: "Find investor firms",
    body: "Get ranked institutional investor firms from CapitalDart's directory, with deliverable contact counts per firm. Browsing firms is free.",
  },
  {
    title: "Build and deliver",
    body: "Turn the plan into a deal on your account and start delivery: CapitalDart identifies the right contact at each matched firm and drafts email and LinkedIn outreach in your voice, delivered to your inbox and dashboard.",
  },
  {
    title: "Manage the raise",
    body: "Track delivery progress, fetch your outreach package, log notes, and update each investor's status from chat. Everything stays in sync with your dashboard at capitaldart.com.",
  },
  {
    title: "Edit anytime",
    body: "Update the deal's description, documents, or targeting from chat, and deliver additional contacts whenever you are ready. Previously delivered firms are excluded automatically.",
  },
  {
    title: "Investor Readiness Report",
    body: "Ask for an AI analysis of your deck: a readiness score, the objections investors will raise, and the changes that answer them, with the full report in your dashboard.",
  },
];

function WhatYouCanAsk() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1100 }}>
        <Kicker style={{ marginBottom: 18 }}>What you can ask</Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(30px,3.4vw,42px)",
            lineHeight: 1.08,
            letterSpacing: "-.01em",
            margin: "0 0 48px",
            maxWidth: 780,
          }}
        >
          Your entire raise, wired to your CapitalDart account.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {CAPABILITIES.map((c) => (
            <div
              key={c.title}
              style={{
                background: PAPER,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "28px 26px",
              }}
            >
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 20,
                  lineHeight: 1.25,
                  margin: "0 0 10px",
                }}
              >
                {c.title}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: INK_2, margin: 0 }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const EXPECT: Array<{ title: string; body: string }> = [
  {
    title: "Approval prompts are normal",
    body: "ChatGPT may show you the details of each request before it calls a tool. That is expected behavior for custom connectors.",
  },
  {
    title: "Enable it per conversation",
    body: "If the assistant says it has no CapitalDart tools, enable the connector from the composer in that conversation, then ask again.",
  },
  {
    title: "Reconnect is safe",
    body: "If a tool reports a connection problem, reconnect the connector in Settings and continue. Reconnecting never loses your work.",
  },
  {
    title: "Never charged twice",
    body: "Contacts you have purchased are never charged again, on any device, in any conversation.",
  },
];

function WhatToExpect() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 1100 }}>
        <Kicker style={{ marginBottom: 18 }}>What to expect</Kicker>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {EXPECT.map((e) => (
            <div
              key={e.title}
              style={{
                background: PAPER_2,
                border: `1px solid ${LINE}`,
                borderRadius: 5,
                padding: "28px 26px",
              }}
            >
              <h3
                style={{
                  fontFamily: FONT_SERIF,
                  fontWeight: 600,
                  fontSize: 19,
                  lineHeight: 1.3,
                  margin: "0 0 10px",
                }}
              >
                {e.title}
              </h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.65, color: INK_2, margin: 0 }}>{e.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CreditsAccount() {
  return (
    <section
      className="px-5 sm:px-8 lg:px-10"
      style={{ background: PAPER_2, padding: "96px 0", borderBottom: `1px solid ${LINE}` }}
    >
      <div className="mx-auto" style={{ maxWidth: 900 }}>
        <Kicker style={{ marginBottom: 18 }}>Credits and account</Kicker>
        <p
          style={{
            fontFamily: FONT_SERIF,
            fontSize: "clamp(20px,2.2vw,26px)",
            lineHeight: 1.4,
            margin: "0 0 26px",
            color: INK,
          }}
        >
          Your ChatGPT connection uses the same account and credit balance as capitaldart.com.
          Buy credits at{" "}
          <a
            href="https://capitaldart.com/buy-credits"
            style={{ color: INK, textDecoration: `underline ${GOLD}` }}
          >
            capitaldart.com/buy-credits
          </a>{" "}
          and they are available in ChatGPT immediately, with no reconnect needed.
        </p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      style={{ background: NAVY, color: NAVY_TEXT, padding: "96px 0", position: "relative" }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(90% 70% at 50% 120%, rgba(201,162,75,.14), rgba(11,37,69,0) 60%)`,
          pointerEvents: "none",
        }}
      />
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{ position: "relative", maxWidth: 900, textAlign: "center" }}
      >
        <Kicker color={GOLD} style={{ marginBottom: 22 }}>
          The full workflow
        </Kicker>
        <h2
          style={{
            fontFamily: FONT_SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px,4.2vw,52px)",
            lineHeight: 1.06,
            letterSpacing: "-.015em",
            margin: "0 0 22px",
            color: "#F7F5EE",
          }}
        >
          Ready for the full workflow?
        </h2>
        <p
          style={{
            fontSize: 17,
            lineHeight: 1.6,
            color: NAVY_TEXT_DIM,
            maxWidth: 640,
            margin: "0 auto 40px",
          }}
        >
          Upload your deck at capitaldart.com for matched investors, outreach drafts, an
          Investor Readiness Report, and a pipeline to track it all.
        </p>
        <GoldButton
          to="/auth"
          search={{ mode: "signup" }}
          style={{ fontSize: 16, padding: "16px 34px" }}
        >
          Get started at capitaldart.com
        </GoldButton>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer style={{ background: NAVY_DEEP, color: NAVY_TEXT_DIM_2, padding: "48px 0" }}>
      <div
        className="mx-auto px-5 sm:px-8 lg:px-10"
        style={{
          maxWidth: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LandingLogo height={26} />
          <span style={{ fontSize: 13.5, color: "#56627A", marginLeft: 14, fontFamily: FONT_SANS }}>
            © 2026
          </span>
        </div>
        <div style={{ display: "flex", gap: 26, fontSize: 14, fontFamily: FONT_SANS, flexWrap: "wrap" }}>
          <Link to="/help" style={{ color: "inherit", textDecoration: "none" }}>
            Help
          </Link>
          <Link to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </Link>
          <Link to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </Link>
          <Link to="/connect" style={{ color: "inherit", textDecoration: "none" }}>
            Use with Claude / ChatGPT
          </Link>
          <Link to="/connect/capabilities" style={{ color: "inherit", textDecoration: "none" }}>
            Claude / ChatGPT capabilities
          </Link>
          <Link to="/partners" style={{ color: "inherit", textDecoration: "none" }}>
            Partners
          </Link>
          <Link to="/lists" style={{ color: "inherit", textDecoration: "none" }}>
            Top 10 Investor Rankings
          </Link>
          <Link to="/contact" style={{ color: "inherit", textDecoration: "none" }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
}

function ConnectChatGptPage() {
  return (
    <main
      id="top"
      style={{ background: PAPER, color: INK, fontFamily: FONT_SANS }}
      className="min-h-screen antialiased overflow-x-clip"
    >
      <style>{`
        @media (max-width: 1023.98px) { .cd-connect-nav-mobile { display: inline-flex !important; } }
        .cd-copy-input:focus { outline: 2px solid ${GOLD}; outline-offset: 2px; }
      `}</style>
      <Hero />
      <Steps />
      <WhatYouCanAsk />
      <WhatToExpect />
      <CreditsAccount />
      <FinalCta />
      <LandingFooter />
    </main>
  );
}
